<script lang="ts" setup>
/* App Global Component
 * This component managed the popper integration and the rendering of the bell icon
 */
import { onMounted, ref, watch } from 'vue';
import Popper from 'vue3-popper';
import PopperContentCe from './PopperContent.ce.vue';
import { useQuery } from '@tanstack/vue-query';
import PanelEnablePushNotifications from './PanelEnablePushNotifications.vue';
import Badge from '@/v1/components/Badge.vue';
import { registerFirebaseCloudMessaging, unregisterFirebaseCloudMessaging } from '@/firebasev2';

const { isSuccess, data } = useQuery({
    queryKey: ['notificationUser'],
    queryFn: async () => {
        const response = await fetch('/notifications_backend/v2/pushtokens/user/id');
        if (response.status !== 200) {
            throw new Error('Failed to fetch notification counter');
        }
        return await response.text();
    },
    refetchOnMount: true,
})

const popperContentRef = ref();

const { isSuccess: countIsSuccess, data: countData, refetch: refetchCount } = useQuery({
    queryKey: ['notificationCounter'],
    queryFn: async () => {
        const response = await fetch('/notifications_backend/v2/notifications/count');
        if (response.status !== 200) {
            throw new Error('Failed to fetch notification counter');
        }
        return Number(await response.text()) || 0;
    },
    refetchOnMount: true,
    refetchInterval: 20_000, // refetch every 20 seconds
})

const displayPushNotificationPrompt = ref(false)

watch([isSuccess, data], async () => {
    if (!isSuccess.value)
        return;
    if (!('Notification' in window))
        return displayPushNotificationPrompt.value = false;
    else if (Notification.permission === 'granted' && localStorage.getItem(`pushnotifications-state-${data.value}`) === 'enabled') {
        const type = await registerFirebaseCloudMessaging();
        if (type === 'disabled' || type === 'err') { // unregister if registration failed or token is disabled
            localStorage.removeItem(`pushnotifications-state-${data.value}`)
            unregisterFirebaseCloudMessaging();
        }
        return displayPushNotificationPrompt.value = false;
    }
    else if (localStorage.getItem(`pushnotifications-state-${data.value}`) === 'disabled') {
        unregisterFirebaseCloudMessaging();
        return displayPushNotificationPrompt.value = false;
    }
    else {
        unregisterFirebaseCloudMessaging();
        return displayPushNotificationPrompt.value = true;
    }
}, {
    immediate: false,
});

// ensure that the panel container is visible
// use panel container to mount PanelEnablePushNotifications component
const panel = document.getElementById('notification-platform-panels');
onMounted(() => {
    if (!panel)
        return;
    panel.style.display = 'block'
})

const bellClassNames = {
    'fa-regular': true,
    'fa-bell': true,
    'lan:text-[27px] lan:px-[5px] lan:text-[#808080] lan:transition-colors lan:duration-200 lan:ease-linear lan:hover:text-[#008bd2]': true,
}


function onPopperShow() {
    popperContentRef.value?.fetchNotifications();
}

function clickSetPushNotificationStateForBrowserInstance(state: 'activate' | 'deactivate') {
    if (state === 'activate') {
        Notification.requestPermission().then(permission => {
            if (permission === 'granted') {
                localStorage.setItem(`pushnotifications-state-${data.value}`, 'enabled');
            } else {
                localStorage.setItem(`pushnotifications-state-${data.value}`, 'disabled');
            }
            displayPushNotificationPrompt.value = false;
        });
    } else if (state === 'deactivate') {
        localStorage.setItem(`pushnotifications-state-${data.value}`, 'disabled');
        displayPushNotificationPrompt.value = false;
    }
}


</script>
<template>
    <div class="notifications-app lan:mr-2">
        <Popper placement="bottom-end" @open:popper="onPopperShow">
            <!-- Popup Button -->
            <div class="lan:flex lan:flex-row lan:flex-nowrap lan:items-center lan:cursor-pointer">
                <div v-if="countIsSuccess && countData! > 0" class="lan:absolute lan:top-0 lan:right-2">
                    <Badge outline>{{ countData! > 9 ? '9+' : countData! }}</Badge>
                </div>
                <div :class="{ 'lan:pr-3': (countData && countData > 9), 'lan:pr-1': !(countData && countData > 9) }">
                    <i :class="bellClassNames" class="lan:block"></i>
                </div>
            </div>
            <template #content>
                <PopperContentCe ref="popperContentRef" @notifications-updated="refetchCount" />
            </template>
        </Popper>
        <Teleport v-if="panel" :to="panel">
            <PanelEnablePushNotifications v-if="displayPushNotificationPrompt"
                @click="clickSetPushNotificationStateForBrowserInstance" />
        </Teleport>
    </div>
</template>